import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { DataStore } from "../custom-functions/dataStore";
import { config } from "../utils/config";

/* global document, Office, module, require, HTMLElement */

// "ResizeObserver loop completed with undelivered notifications" is a
// spec-defined benign signal emitted by layout-measuring libraries (Fluent UI
// Dropdown, @floating-ui). Layout still completes on the next frame. We stop
// propagation so it doesn't reach error reporters or the dev-server overlay.
const resizeObserverErr = /ResizeObserver loop/i;

window.addEventListener("error", (e) => {
  if (resizeObserverErr.test(e.message) || resizeObserverErr.test(e.error?.stack || "")) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = String(e.reason?.message ?? "");
  const stack = String(e.reason?.stack ?? "");
  if (resizeObserverErr.test(msg) || resizeObserverErr.test(stack)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

/* Render application after Office initializes */
Office.onReady(() => {
  root?.render(
    <FluentProvider theme={webLightTheme}>
      <App />
    </FluentProvider>
  );

  // Register Office document handlers only after Office has initialized.
  Office.context.document?.addHandlerAsync?.(Office.EventType.DocumentSelectionChanged, () => {
    // Document still active, do nothing
  });

  // Dev-only: expose the IGD parity checker on the console. Run it in an Excel
  // that has BOTH the COM add-in and this add-in loaded:
  //   await __runIgdValidation(['AAPL', 'MSFT'])
  // if (config.IS_PROD !== 1) {
  //   void import("../features/dev/igdValidationService").then((mod) => {
  //     (window as any).__runIgdValidation = mod.runIgdValidation;
  //   });
  // }
});

// NOTE: this used to clear the IAD cache on beforeunload and on visibilitychange
// (hidden). Both fire on a normal pane switch — each ribbon button opens its own
// taskpane.html?pane=... URL, so Office navigates the pane — which meant every pane
// switch threw away the cache and left all IAD_OFJ formulas refetching from the
// network. DataStore now mirrors itself to localStorage and rehydrates on load, so
// clearing here would defeat that. Refresh and logout still clear explicitly via
// DataStore.clearAll().

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(NextApp);
  });
}
